import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await resetPassword({ username: email });
      setStep('code');
      setSuccess('Código de verificação enviado para seu email.');
    } catch (err: any) {
      const msg = err.message || '';
      if (msg.includes('no registered/verified email') || msg.includes('Unable to send')) {
        setError('O serviço de email para redefinição de senha ainda não está configurado. Entre em contato com o administrador.');
      } else if (msg.includes('User does not exist') || msg.includes('UserNotFoundException')) {
        setError('Email não encontrado. Verifique o email informado.');
      } else if (msg.includes('Attempt limit exceeded')) {
        setError('Muitas tentativas. Aguarde alguns minutos e tente novamente.');
      } else {
        setError(msg || 'Erro ao enviar código. Verifique o email informado.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      await confirmResetPassword({ username: email, confirmationCode: code, newPassword });
      setSuccess('Senha alterada com sucesso!');
      setTimeout(() => navigate('/login'), 2000);
    } catch (err: any) {
      setError(err.message || 'Erro ao redefinir senha. Verifique o código e tente novamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">Redefinir Senha</CardTitle>
          <CardDescription className="text-center">
            {step === 'email'
              ? 'Informe seu email para receber o código de verificação'
              : 'Digite o código recebido e sua nova senha'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-md mb-4">{error}</div>
          )}
          {success && (
            <div className="bg-green-50 text-green-700 text-sm p-3 rounded-md mb-4">{success}</div>
          )}

          {step === 'email' ? (
            <form onSubmit={handleSendCode} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">Email</label>
                <Input
                  id="email" type="email" placeholder="seu@email.com"
                  value={email} onChange={e => setEmail(e.target.value)}
                  required disabled={isLoading}
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Enviando...' : 'Enviar Código'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="code" className="text-sm font-medium">Código de Verificação</label>
                <Input
                  id="code" placeholder="123456"
                  value={code} onChange={e => setCode(e.target.value)}
                  required disabled={isLoading}
                />
              </div>
              <div className="space-y-2">
                <label htmlFor="newPassword" className="text-sm font-medium">Nova Senha</label>
                <Input
                  id="newPassword" type="password" placeholder="••••••••"
                  value={newPassword} onChange={e => setNewPassword(e.target.value)}
                  required disabled={isLoading}
                />
              </div>
              <Button type="submit" className="w-full" disabled={isLoading}>
                {isLoading ? 'Redefinindo...' : 'Redefinir Senha'}
              </Button>
            </form>
          )}

          <div className="mt-4 text-center">
            <a href="/login" className="text-sm text-primary hover:underline">Voltar ao login</a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
